import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Loader2, Mail, User as UserIcon, Store, ShieldCheck, Fingerprint, Copy } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const UserRow = ({ u, onOpen }) => (
  <tr
    key={u.user_id}
    className="border-b border-gray-100 hover:bg-tbc-gold/5 cursor-pointer transition-colors"
    onClick={() => onOpen(u)}
    data-testid={`admin-user-row-${u.user_id}`}
  >
    <td className="py-3 px-3">
      <div className="flex items-center gap-3">
        {u.avatar_url ? (
          <img src={u.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-tbc-navy/10 text-tbc-navy flex items-center justify-center font-semibold">
            {(u.name || u.email || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <div className="font-medium text-tbc-navy flex items-center gap-2">
            {u.name || <span className="text-gray-400 italic">no name</span>}
            {u.is_admin && (
              <Badge className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold text-xs">
                <ShieldCheck className="w-3 h-3 mr-1" />Admin
              </Badge>
            )}
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Mail className="w-3 h-3" />
            {u.email || "—"}
          </div>
        </div>
      </div>
    </td>
    <td className="py-3 px-3 text-sm">
      <Badge variant="outline" className="capitalize">
        {u.user_type || "customer"}
      </Badge>
    </td>
    <td className="py-3 px-3 text-sm">
      <Badge variant="outline" className={u.auth_method === "google" ? "border-blue-300 text-blue-700" : "border-gray-300 text-gray-600"}>
        {u.auth_method === "google" ? "Google" : "Email/PW"}
      </Badge>
    </td>
    <td className="py-3 px-3 text-sm">
      <span className="text-tbc-navy font-medium">
        {u.businesses_owned?.count || 0}
      </span>
    </td>
    <td className="py-3 px-3 text-xs text-gray-500">{formatDate(u.created_at)}</td>
  </tr>
);

const UserDetailDialog = ({ userId, open, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId || !open) return;
    setLoading(true);
    axios.get(`${API}/admin/users/${userId}`)
      .then((r) => setDetail(r.data))
      .catch(() => toast.error("Failed to load user profile"))
      .finally(() => setLoading(false));
  }, [userId, open]);

  const copy = (val, label) => {
    navigator.clipboard.writeText(val || "");
    toast.success(`${label} copied`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">User Profile</DialogTitle>
        </DialogHeader>
        {loading || !detail ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-5" data-testid="admin-user-detail">
            <div className="flex items-center gap-4">
              {detail.user.avatar_url ? (
                <img src={detail.user.avatar_url} alt="" className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-tbc-navy/10 text-tbc-navy flex items-center justify-center text-2xl font-semibold">
                  {(detail.user.name || detail.user.email || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-xl font-heading text-tbc-navy flex items-center gap-2">
                  {detail.user.name || "—"}
                  {detail.user.is_admin && (
                    <Badge className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold">
                      <ShieldCheck className="w-3 h-3 mr-1" />Admin
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" />
                  {detail.user.email}
                  <button onClick={() => copy(detail.user.email, "Email")} className="text-tbc-navy/60 hover:text-tbc-navy" title="Copy email">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">User Type</div>
                <div className="capitalize font-medium">{detail.user.user_type || "customer"}</div>
              </div>
                <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Auth Method</div>
                <div className="font-medium">
                  {detail.user.auth_method === "google" ? "Google OAuth" : "Email + Password"}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Phone</div>
                <div className="font-medium">{detail.user.phone || "—"}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Joined</div>
                <div className="font-medium">{formatDate(detail.user.created_at)}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 col-span-2">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-1">
                  <Fingerprint className="w-3 h-3" /> User ID
                </div>
                <div className="font-mono text-xs text-tbc-navy break-all flex items-center gap-2">
                  {detail.user.user_id}
                  <button onClick={() => copy(detail.user.user_id, "User ID")} className="text-tbc-navy/60 hover:text-tbc-navy" title="Copy ID">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
              {detail.user.referral_code && (
                <div className="p-3 rounded-lg bg-gray-50 col-span-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Referral Code</div>
                  <div className="font-mono font-medium text-tbc-navy">{detail.user.referral_code}</div>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-heading text-lg text-tbc-navy mb-2 flex items-center gap-2">
                <Store className="w-4 h-4" /> Businesses Owned ({detail.businesses.length})
              </h3>
              {detail.businesses.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No businesses.</p>
              ) : (
                <ul className="space-y-2">
                  {detail.businesses.map((b) => (
                    <li key={b.business_id} className="p-3 rounded-lg border border-gray-100 hover:border-tbc-gold/50 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <a
                            href={`/business/${b.business_id}`}
                            target="_blank" rel="noreferrer"
                            className="font-medium text-tbc-navy hover:text-tbc-gold"
                          >
                            {b.name}
                          </a>
                          <div className="text-xs text-gray-500">
                            {b.category} · {b.city || "—"}, {b.state || "—"}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant="outline" className="capitalize text-xs">
                            {b.subscription_tier || "free"}
                          </Badge>
                          {b.is_verified && <Badge className="bg-tbc-gold/20 text-tbc-navy text-xs">Verified</Badge>}
                          {b.is_founding_member && <Badge className="bg-tbc-navy text-white text-xs">Founding</Badge>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-tbc-navy/5">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Orders Placed</div>
                <div className="text-xl font-heading text-tbc-navy">{detail.activity.orders}</div>
              </div>
              <div className="p-3 rounded-lg bg-tbc-navy/5">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Reviews Written</div>
                <div className="text-xl font-heading text-tbc-navy">{detail.activity.reviews}</div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminUsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [userType, setUserType] = useState("all");
  const [total, setTotal] = useState(0);
  const [openUserId, setOpenUserId] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (userType !== "all") params.set("user_type", userType);
      params.set("limit", "200");
      const r = await axios.get(`${API}/admin/users?${params}`);
      setUsers(r.data.users || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [q, userType]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-heading text-tbc-navy flex items-center gap-2">
            <UserIcon className="w-5 h-5" /> Users
          </h2>
          <p className="text-sm text-gray-500">Everyone who&apos;s signed up · {total} total</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search email or name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchUsers()}
              className="pl-9 w-64"
              data-testid="admin-users-search"
            />
          </div>
          <Select value={userType} onValueChange={setUserType}>
            <SelectTrigger className="w-40" data-testid="admin-users-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="business_owner">Business Owners</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchUsers} variant="outline" data-testid="admin-users-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        {loading && users.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading users…
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-gray-500">No users match your filters.</div>
        ) : (
          <table className="w-full text-left">
            <thead className="text-xs uppercase text-gray-500 border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="py-3 px-3">User</th>
                <th className="py-3 px-3">Type</th>
                <th className="py-3 px-3">Auth</th>
                <th className="py-3 px-3">Businesses</th>
                <th className="py-3 px-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow key={u.user_id} u={u} onOpen={(user) => setOpenUserId(user.user_id)} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UserDetailDialog
        userId={openUserId}
        open={!!openUserId}
        onClose={() => setOpenUserId(null)}
      />
    </div>
  );
}
